import { useEffect, useState } from "react";
import api from "@/lib/api";
import { logger } from "@/lib/logger";

const LOCATION_KEY = "rivendas_location";

/**
 * Hook to obtain/refresh browser geolocation and sync with backend.
 * Returns { coords, status, request } where status is:
 *  - "idle" — not asked yet
 *  - "prompt" — waiting user permission
 *  - "granted" — coords available
 *  - "denied" — user denied
 *  - "unavailable" — no browser support
 */
export default function useGeoLocation() {
  const [coords, setCoords] = useState(null);
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    try {
      const raw = localStorage.getItem(LOCATION_KEY);
      if (raw) {
        const c = JSON.parse(raw);
        if (c?.lat && c?.lng) {
          setCoords(c);
          setStatus("granted");
        }
      }
    } catch (err) {
      logger.warn("geo: unable to read stored coords", err?.message);
    }
  }, []);

  const request = () => {
    if (!navigator.geolocation) {
      setStatus("unavailable");
      return Promise.reject(new Error("Geolocation não suportada"));
    }
    setStatus("prompt");
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setCoords(c);
          setStatus("granted");
          try {
            localStorage.setItem(LOCATION_KEY, JSON.stringify(c));
          } catch (err) {
            logger.warn("geo: persist", err?.message);
          }
          try {
            await api.patch("/auth/location", c);
          } catch (err) {
            logger.warn("geo: sync backend", err?.message);
          }
          resolve(c);
        },
        (err) => {
          setStatus(err.code === err.PERMISSION_DENIED ? "denied" : "unavailable");
          reject(err);
        },
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 3600 * 1000 }
      );
    });
  };

  return { coords, status, request };
}
